import React, { useContext, useEffect, useState, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Form } from 'semantic-ui-react';
import { get } from 'lodash';
import { latLngToMGRS } from 'src/utils/useful_functions';
import { parseCoords } from 'src/pages/map/SiteModePanel';
import { FormResetContext } from 'src/pages/pmp/utils';

interface MGRSCoordinateInputProps {
  site: any;
  setModified: (modified: boolean) => void;
  disabled: boolean;
  latitudeField?: string; // Optional: defaults to 'latitude'
  longitudeField?: string; // Optional: defaults to 'longitude'
  mgrsReferenceField?: string; // Optional: defaults to 'mgrs_reference'
}

export const MGRSCoordinateInput: React.FC<MGRSCoordinateInputProps> = ({
  site,
  setModified,
  disabled,
  latitudeField = 'latitude',
  longitudeField = 'longitude',
  mgrsReferenceField = 'mgrs_reference',
}) => {
  const {
    control,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();
  const resetValue = useContext(FormResetContext);

  // Watch latitude and longitude from form
  const formLat = useWatch({ control, name: latitudeField });
  const formLng = useWatch({ control, name: longitudeField });
  const gridReference = useWatch({ control, name: mgrsReferenceField });

  // Use form values if available, otherwise fall back to site values
  const currentLat = formLat ?? site.latitude;
  const currentLng = formLng ?? site.longitude;

  // Calculate reference value for display (initial/default value)
  const mgrsReferenceValue = latLngToMGRS(currentLat, currentLng);

  // Track if user is actively editing to prevent overwriting their input
  const [isUserEditing, setIsUserEditing] = useState(false);
  const previousResetValue = useRef(resetValue);

  // Update reference field when form is reset (resetValue changes)
  // This ensures reference field is recalculated from site lat/lon on reset
  // Only update when resetValue actually changes (user clicked reset), not during normal editing
  useEffect(() => {
    if (previousResetValue.current !== resetValue && !isUserEditing && site.latitude != null && site.longitude != null) {
      const newMGRSRef = latLngToMGRS(site.latitude, site.longitude);
      setValue(mgrsReferenceField, newMGRSRef, { shouldValidate: false });
      previousResetValue.current = resetValue;
    }
  }, [resetValue, site.latitude, site.longitude, setValue, mgrsReferenceField, isUserEditing]);

  const error = get(errors, mgrsReferenceField);

  return (
    <Form.Group widths="equal">
      <Controller
        control={control}
        name={mgrsReferenceField}
        defaultValue={gridReference || mgrsReferenceValue}
        rules={{
          required: true,
          validate: {
            mgrs: (value) => {
              if (!value || value.trim() === '') {
                return 'MGRS coordinate is required';
              }
              const trimmedValue = value.trim();
              const { valid } = parseCoords(trimmedValue);
              if (!valid) {
                return 'Invalid MGRS coordinate';
              }
              return true;
            },
          },
        }}
        render={({ field: { ref, onChange, value, ...rest } }) => (
          <Form.Field required>
            <label>Grid Reference:</label>
            <Form.Input
              {...rest}
              ref={ref}
              type="text"
              value={value || ''}
              error={error != null}
              disabled={disabled}
              onChange={(e, { value: newValue }) => {
                setIsUserEditing(true);
                onChange(newValue);
                setModified(true);
                const trimmedValue = newValue.trim();
                if (trimmedValue.length > 0) {
                  const { valid: newValid, lat: newLat, lng: newLng } = parseCoords(trimmedValue);
                  // MGRS coordinates are typically 15 characters, so only update if it's reasonably complete
                  if (newValid && newLat != null && newLng != null && trimmedValue.length >= 10) {
                    setValue(latitudeField, newLat, { shouldValidate: false });
                    setValue(longitudeField, newLng, { shouldValidate: false });
                  }
                }
                setTimeout(() => {
                  trigger(mgrsReferenceField);
                  // Reset editing flag after a delay to allow reset to work
                  setTimeout(() => {
                    setIsUserEditing(false);
                  }, 500);
                }, 0);
              }}
              onBlur={() => {
                // Reset editing flag when user leaves the field
                setTimeout(() => {
                  setIsUserEditing(false);
                }, 100);
              }}
            />
          </Form.Field>
        )}
      />
    </Form.Group>
  );
};
